import { Alert, AlertColor, Box, Button, CircularProgress, DialogActions, DialogContent, TextField, Typography } from "@mui/material";
import { formatCurrency } from "../../../utils/formatCurrency";
import { MbwayRequest, isOpenMbwayRequest } from "./mbwayPaymentApi";
import { buildMbwayRequestMessage, ORDER_PAID_ELSEWHERE_REASON } from "./mbwayPaymentMessages";
import { useMbwayPayment } from "./useMbwayPayment";
import { SplitPaymentRows } from "../split-payment/SplitPaymentRows";
import { ChargeProgress, buildSplitPaymentsNote } from "../split-payment/splitPayments";
import { useSplitPayments } from "../split-payment/useSplitPayments";

const MBWAY_PAYMENT_METHOD = "online_payment";

type MbwayPaymentDialogProps = {
    orderId: number;
    orderTotal: number;
    clientPhone: string | null;
    treatmentId: number | null;
    onPaid: () => void;
    onClose: () => void;
};

const RESULT_SEVERITY: Record<MbwayRequest["status"], AlertColor> = {
    pending: "info",
    paid: "success",
    refused: "warning",
    expired: "warning",
    declined: "error",
    failed: "error",
    replaced: "info",
};

const resolveChargeProgress = (request: MbwayRequest) => {
    if (isOpenMbwayRequest(request)) return "open";
    const chargeProgress: ChargeProgress = request.status === "paid" ? "paid" : "closed";
    return chargeProgress;
};

const formatCountdown = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    const countdown = `${minutes}:${seconds}`;
    return countdown;
};

const canRequestAgain = (request: MbwayRequest | null) => {
    const isFinishedForGood = request?.status === "paid" || request?.failure_reason === ORDER_PAID_ELSEWHERE_REASON;
    const canRequest = !isFinishedForGood;
    return canRequest;
};

export const MbwayPaymentDialog = ({ orderId, orderTotal, clientPhone, treatmentId, onPaid, onClose }: MbwayPaymentDialogProps) => {
    const mbwayPayment = useMbwayPayment(orderId, clientPhone, treatmentId, onPaid);
    const { request, screen } = mbwayPayment;
    const splitPayments = useSplitPayments(orderTotal, MBWAY_PAYMENT_METHOD, screen === "phone", request);
    const requestSplitPayments = request?.split_payments ?? [];

    return (
        <>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {screen === "loading" && (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress />
                    </Box>
                )}

                {screen === "phone" && (
                    <>
                        <Typography variant="h6">
                            {splitPayments.rows.length
                                ? `Valor a pedir por MB WAY: ${formatCurrency(splitPayments.remainingAmount)}`
                                : `Valor a cobrar: ${formatCurrency(orderTotal)}`}
                        </Typography>
                        <SplitPaymentRows rows={splitPayments.rows} onChange={splitPayments.setRows} />
                        {splitPayments.visibleError && <Alert severity="warning">{splitPayments.visibleError}</Alert>}
                        <TextField
                            label="Telemóvel do cliente"
                            value={mbwayPayment.phone}
                            onChange={(event) => mbwayPayment.changePhone(event.target.value)}
                            inputProps={{ inputMode: "tel" }}
                            helperText="O cliente recebe o pedido na app MB WAY e tem 4 minutos para aceitar."
                            fullWidth
                        />
                    </>
                )}

                {screen === "waiting" && request && (
                    <Box display="flex" alignItems="center" gap={2}>
                        <CircularProgress size={28} />
                        <Box>
                            <Typography variant="body1">{buildMbwayRequestMessage(request)}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Tempo restante: {formatCountdown(mbwayPayment.secondsRemaining)}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {screen === "result" && request && <Alert severity={RESULT_SEVERITY[request.status]}>{buildMbwayRequestMessage(request)}</Alert>}

                {screen !== "phone" && request && requestSplitPayments.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {buildSplitPaymentsNote(requestSplitPayments, resolveChargeProgress(request), request.recording_status)}
                    </Typography>
                )}

                {mbwayPayment.errorMessage && <Alert severity="error">{mbwayPayment.errorMessage}</Alert>}
            </DialogContent>

            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    Fechar
                </Button>
                {screen === "phone" && (
                    <Button
                        onClick={() => mbwayPayment.sendRequest(splitPayments.payments)}
                        disabled={mbwayPayment.isSubmitting || !mbwayPayment.phone || !splitPayments.validation.isValid}
                    >
                        {mbwayPayment.isSubmitting ? <CircularProgress color="inherit" size={24} /> : "Enviar pedido MB WAY"}
                    </Button>
                )}
                {screen === "waiting" && (
                    <Button color="error" onClick={mbwayPayment.replaceRequest} disabled={mbwayPayment.isReplacing}>
                        Número errado?
                    </Button>
                )}
                {screen === "result" && canRequestAgain(request) && <Button onClick={mbwayPayment.openPhoneForm}>Tentar novamente</Button>}
            </DialogActions>
        </>
    );
};
